import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

interface CacheSettings {
  useCache: boolean;
  useDeltas: boolean;
  rebuildCache: boolean;
}

interface CacheSettingsCellProps {
  value: CacheSettings;
  onSave: (settings: CacheSettings) => void;
  disabled?: boolean;
}

const cacheSwitches: { key: keyof CacheSettings; label: string }[] = [
  { key: "useCache", label: "Cache" },
  { key: "useDeltas", label: "Deltas" },
  { key: "rebuildCache", label: "Rebuild" },
];

const settingsEqual = (a: CacheSettings, b: CacheSettings) =>
  a.useCache === b.useCache
  && a.useDeltas === b.useDeltas
  && a.rebuildCache === b.rebuildCache;

export const CacheSettingsCell: React.FC<CacheSettingsCellProps> = ({ value, onSave, disabled = false }) => {
  const [local, setLocal] = useState<CacheSettings>(value);

  useEffect(() => {
    setLocal(value);
  }, [value.useCache, value.useDeltas, value.rebuildCache]);

  const isDirty = !settingsEqual(local, value);

  const handleChange = (key: keyof CacheSettings, checked: boolean) => {
    setLocal((prev) => {
      const next = { ...prev, [key]: checked };
      if (key === "useCache" && !checked) {
        next.useDeltas = false;
        next.rebuildCache = false;
      }
      if (key === "useCache" && checked) {
        next.rebuildCache = true;
      }
      return next;
    });
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-col gap-0.5">
        {cacheSwitches.map(({ key, label }) => {
          const isDisabled =
            (key !== "useCache" && !local.useCache) ||
            (key === "rebuildCache" && local.rebuildCache) ||
            disabled;

          return (
            <div key={key} className="flex items-center gap-1">
              <Switch
                size="sm"
                checked={local[key]}
                onCheckedChange={(checked) => handleChange(key, checked)}
                disabled={isDisabled}
              />
              <Label
                className={`text-xs font-normal m-0 ${isDisabled ? "pointer-events-none opacity-50" : ""}`}
              >
                {label}
              </Label>
            </div>
          );
        })}
      </div>
      {isDirty && !disabled && (
        <Button
          variant="default"
          size="xs"
          onClick={() => onSave(local)}
          className="self-start w-[72px]"
        >
          Save
        </Button>
      )}
    </div>
  );
};
